import React, { useEffect, useRef, useState } from "react";
import { MoreVertical, Download, Trash2, X, Mic, HelpCircle } from "lucide-react";


export default function CoverDetailView({ cover, onClose, onDelete }) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleLoadedMetadata = () => setDuration(audio.duration);

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [cover.output_audio_url]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [menuOpen]);

  const handleDownload = () => {
    if (cover.output_audio_url) {
      const link = document.createElement('a');
      link.href = cover.output_audio_url;
      link.download = `${cover.title || 'cover'}.mp3`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    setMenuOpen(false);
  };

  const handleDelete = () => {
    if (window.confirm('Delete this cover?')) {
      onDelete && onDelete(cover.id);
    }
    setMenuOpen(false);
  };

  const getAccuracyColor = (accuracy) => {
    switch (accuracy) {
      case 'Great': return '#3b82f6';
      case 'Good': return '#22c55e';
      case 'Okey': return '#eab308';
      case 'TooLow':
      case 'TooHigh': return '#ef4444';
      default: return '#9ca3af';
    }
  };

  const getAccuracyLabel = (accuracy) => {
    switch (accuracy) {
      case 'TooLow': return 'Too Low';
      case 'TooHigh': return 'Too High';
      default: return accuracy || 'Unknown';
    }
  };

  return (
    <div className="panel panel-detail panel-hug" data-testid="cover-detail-panel">
      <div className="panel-title-row">
        <h2 className="panel-title">Cover</h2>
        <div className="panel-title-actions">
          <div className="detail-menu-container" ref={menuRef}>
            <button
              className="btn-icon"
              onClick={() => setMenuOpen(!menuOpen)}
              data-testid="cover-detail-menu-btn"
            >
              <MoreVertical size={20} />
            </button>
            {menuOpen && (
              <div className="detail-menu" data-testid="cover-detail-menu">
                <button className="detail-menu-item" onClick={handleDownload} data-testid="menu-download-cover">
                  <Download size={16} />
                  <span>Download</span>
                </button>
                <div className="detail-menu-divider" />
                <button className="detail-menu-item detail-menu-danger" onClick={handleDelete} data-testid="menu-delete-cover">
                  <Trash2 size={16} />
                  <span>Delete</span>
                </button>
              </div>
            )}
          </div>
          <button className="btn-icon" onClick={onClose} data-testid="close-cover-detail">
            <X size={20} />
          </button>
        </div>
      </div>

      <div className="song-detail-layout-v2">
        <div className="song-detail-cover">
          <div className="song-detail-cover-fallback" style={{ display: 'flex' }}>
            <Mic size={48} />
          </div>
        </div>

        <h3 className="song-detail-title">{cover.title || 'Cover'}</h3>

        <div className="song-detail-styles styles-collapsed">
          <div className="styles-content">Voice: {cover.voice_model_name}</div>
        </div>

        {cover.output_audio_url && (
          <div className="song-detail-player">
            <audio
              ref={audioRef}
              controls
              src={cover.output_audio_url}
              className="audio-full"
              preload="metadata"
            />
          </div>
        )}

        <div className="cover-details-section">
          <div className="cover-details-label">Details</div>
          <div className="cover-details-row">
            <span className="cover-details-key">Source File</span>
            <span className="cover-details-value">{cover.source_audio_file || 'Unknown'}</span>
          </div>
          <div className="cover-details-row">
            <span className="cover-details-key">Created</span>
            <span className="cover-details-value">{new Date(cover.created_at).toLocaleDateString()}</span>
          </div>
          <div className="cover-details-row">
            <span className="cover-details-key">Voice Pitch</span>
            <span className="cover-details-value cover-pitch-value">
              <span style={{ color: getAccuracyColor(cover.accuracy) }}>
                {getAccuracyLabel(cover.accuracy)}
              </span>
              <span className="cover-pitch-help" data-tooltip="Voice pitch is the vocal range, indicating how naturally the voice sounds higher or lower for this song.">
                <HelpCircle size={16} />
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
